import { useState } from "react";
import useModal from "@/hooks/useModal";
import { Controller, useForm, useFieldArray } from "react-hook-form";
import DateSelect from "@/components/DateSelect";
import { Control, useController } from "react-hook-form";
import { DateValueType } from "react-tailwindcss-datepicker";
import Button from "@/components/Button";
import { FormProvider } from "react-hook-form";
import { FieldPathByValue } from "react-hook-form";
import NumericField from "./NumericField";
import { ControlledTextField as TextField } from "@/components/TextField";
import { DateTime } from "luxon";
import Checkbox from "@/components/Checkbox";
import Select from "@/components/Select";
import { CycleRequirements, AutoCohort } from "@/types";
import Banner from "@/components/Banner";
import { sumBy, find } from "lodash";
import { useEffect } from "react";
import { useMutation, useQuery } from "@apollo/client";
import EmailPreviewDialog, {
  EMAIL_PREVIEW_MODAL_CLASSNAME,
  EmailPreviewModalActions
} from "@/components/EmailPreviewDialog";
import {
  GET_PDR_DEFAULT_REQUIREMENT_STIPULATIONS_QUERY,
  UPSERT_PDR_REQUIREMENTS_MUTATION,
  GET_PDR_REQUIREMENTS_QUERY
} from "../../queries";
import { useParams } from "react-router-dom";
import { showSnackbar } from "@/lib/snackbarUtils";
import { ApolloError } from "@apollo/client/errors";
import { PdrDefaultRequirementStipulationsQuery, Stipulation } from "../types";
import { usePdrRequirementAdjustmentEmailPreview } from "../hooks/usePdrRequirementAdjustmentEmailPreview";

const AUTO_COHORT_MAP = {
  SD: "Standard Requirements",
  IN: "International or RSV",
  NO: "No Requirements",
  NT: "90+ Years of Service/Age"
} as const;

const AUTO_COHORT_CODES = Object.keys(AUTO_COHORT_MAP);

export type FieldValues = {
  cohort: string;
  effectiveDate: string;
  requirements: {
    requiredCredits: string;
    deficit: string;
    earned: number;
    stipulationCode: string;
    label: string;
  }[];

  notifyMember: boolean;
  notes: string;
};

function SingleDatePicker({
  control,
  label,
  required = false,
  name,
  minDate,
  maxDate
}: {
  control: Control<FieldValues>;
  label: string;
  required?: boolean;
  name: FieldPathByValue<FieldValues, string>;
  minDate: string;
  maxDate: string;
}) {
  const {
    field: { onChange, value },
    fieldState: { error }
  } = useController({
    control,
    name,
    rules: {
      validate: (value) => {
        if (!value) {
          return `${label} is required`;
        }
        if (maxDate && minDate) {
          const selectedDate = DateTime.fromISO(value).startOf("day");
          const maxDateTime = DateTime.fromISO(maxDate).startOf("day");
          const minDateTime = DateTime.fromISO(minDate).startOf("day");

          if (selectedDate > maxDateTime || selectedDate < minDateTime) {
            return `Effective date must be between ${minDateTime.toFormat("MM/dd/yyyy")} and ${maxDateTime.toFormat(
              "MM/dd/yyyy"
            )}`;
          }
        }

        const dateRegex = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
        if (!dateRegex.test(value)) {
          return "Date must be in MM/DD/YYYY format";
        }

        const [year, month, day] = value.split("-").map(Number);
        const date = new Date(year, month - 1, day);
        if (date.getMonth() !== month - 1 || date.getDate() !== day || date.getFullYear() !== year) {
          return "Please enter a valid date";
        }

        return true;
      }
    }
  });

  return (
    <DateSelect
      label={required ? `${label} *` : label}
      value={{ startDate: value, endDate: value }}
      useModal={true}
      onChange={(x: DateValueType | string) => {
        if (typeof x === "string") return;

        if (x === null) {
          onChange("");
          return;
        }

        onChange(x.startDate);
      }}
      useSingleDate
      useMultiCalendar={false}
      error={!!error}
      errorMessage={error?.message}
      minDate={DateTime.fromISO(minDate).toJSDate()}
      maxDate={DateTime.fromISO(maxDate).toJSDate()}
    />
  );
}

function getLatestDate(effectiveDate?: string): string {
  const today = DateTime.now().startOf("day");
  if (!effectiveDate) return today.toFormat("yyyy-MM-dd");
  const reqEffDate = DateTime.fromISO(effectiveDate).startOf("day");
  const latestDate = reqEffDate > today ? reqEffDate : today;
  return latestDate.toFormat("yyyy-MM-dd");
}

function getRequirementsAdjustmentNote(
  currentTotalCredits: number,
  earnedCredits: number,
  newTotalCredits: number,
  isCohortChanged: boolean
): string | null {
  const willMeetRequirements = earnedCredits >= newTotalCredits;
  const currentlyMeetsRequirements = earnedCredits >= currentTotalCredits;
  if (!currentlyMeetsRequirements && willMeetRequirements) {
    return "Note: This person will have met their requirements if these adjustments are saved";
  } else if (currentlyMeetsRequirements && !willMeetRequirements) {
    return "Note: This person will have no longer met their requirements if these adjustments are saved";
  }
  if (isCohortChanged && newTotalCredits !== currentTotalCredits) {
    return `Note: This person will have ${
      newTotalCredits - currentTotalCredits > 0 ? "more" : "less"
    } requirements if these adjustments are saved`;
  }
  return null;
}

export default function AdjustRequirementsModal({
  onDismiss,
  cycleRequirements = {
    cohortCode: "SD",
    cohort: "",
    cycleCode: "18",
    requirements: [],
    cycleStartsOn: "",
    cycleEndsOn: "",
    status: "",
    requirementsEffectiveDate: ""
  }
}: {
  onDismiss: () => void;
  cycleRequirements?: CycleRequirements;
}) {
  const isNewRequirements = cycleRequirements.requirements.length === 0;
  const { pgaId } = useParams();
  const [autoCohortLabel, setAutoCohortLabel] = useState<AutoCohort | null>(null);
  const [bannerError, setBannerError] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const {
    loadPreview,
    resetPreview,
    preview,
    loading: previewLoading,
    error: previewError
  } = usePdrRequirementAdjustmentEmailPreview();

  useEffect(() => {
    return () => resetPreview();
  }, [resetPreview]);
  const autoCohort = AUTO_COHORT_CODES.includes(cycleRequirements.cohortCode as AutoCohort)
    ? "AUTO"
    : cycleRequirements.cohortCode;
  const isCycle17 = cycleRequirements.cycleCode === "17";
  const cohortOptions = isCycle17
    ? [
        {
          displayText: cycleRequirements.cohort,
          value: cycleRequirements.cohortCode
        }
      ]
    : [
        {
          displayText: `Automatic ${
            autoCohortLabel && AUTO_COHORT_CODES.includes(autoCohortLabel)
              ? `(${AUTO_COHORT_MAP[autoCohortLabel]})`
              : ""
          }`,
          value: "AUTO"
        },
        {
          displayText: "Override",
          value: "OR"
        }
      ];

  const formDefaultValues = {
    cohort: cohortOptions.find((option) => option.value === autoCohort)?.value,
    effectiveDate: getLatestDate(cycleRequirements.requirementsEffectiveDate),
    requirements: cycleRequirements.requirements.map((requirement) => ({
      stipulationCode: requirement.stipulationCode,
      earned: requirement.earned,
      requiredCredits: String(requirement.cycleRequirement),
      deficit: String(requirement.deficit),
      label: requirement.label
    })),
    notifyMember: true,
    notes: ""
  };

  const { Modal } = useModal();
  const form = useForm<FieldValues>({
    defaultValues: formDefaultValues,
    mode: "onChange"
  });

  const {
    control,
    watch,
    handleSubmit,
    setValue,
    resetField,
    getValues,
    formState: { isSubmitting, isDirty, errors }
  } = form;

  const { fields, replace } = useFieldArray({
    control,
    name: "requirements"
  });

  const cohort = watch("cohort");
  const effectiveDate = watch("effectiveDate");
  const notifyMember = watch("notifyMember");
  const canOverrideRequirements = isCycle17 || cohort === "OR";
  const canPreviewEmail = notifyMember && Boolean(pgaId) && Boolean(cycleRequirements.cycleCode);

  const handlePreviewEmail = () => {
    if (!canPreviewEmail || !pgaId) {
      return;
    }

    setPreviewOpen(true);
    void loadPreview({
      pgaId,
      cycleCode: cycleRequirements.cycleCode
    });
  };

  const { data, loading } = useQuery<PdrDefaultRequirementStipulationsQuery>(
    GET_PDR_DEFAULT_REQUIREMENT_STIPULATIONS_QUERY,
    {
      variables: {
        pgaId: pgaId,
        cycleCode: cycleRequirements.cycleCode,
        effectiveDate: DateTime.fromISO(effectiveDate).toFormat("yyyy-MM-dd"),
        cohortCode: cohort === "AUTO" ? null : cohort
      },
      skip: isCycle17
    }
  );

  const [upsertPdrRequirements] = useMutation(UPSERT_PDR_REQUIREMENTS_MUTATION, {
    refetchQueries: [
      {
        query: GET_PDR_REQUIREMENTS_QUERY,
        variables: {
          pgaId,
          cycleCode: cycleRequirements.cycleCode
        }
      }
    ]
  });

  const {
    pdrDefaultRequirementStipulations: { success: successStipulations = false, cohortDiffersFromToday = false } = {}
  } = data || {};
  const note =
    !isNewRequirements &&
    getRequirementsAdjustmentNote(
      sumBy(formDefaultValues.requirements, (r) => Number(r.requiredCredits)),
      sumBy(formDefaultValues.requirements, (r) => Number(r.earned)),
      sumBy(watch("requirements"), (r) => Number(r.requiredCredits)),
      cohort !== formDefaultValues.cohort && !canOverrideRequirements
    );
  const onSubmit = async (values: FieldValues) => {
    try {
      const {
        data: {
          upsertPdrRequirements: { success, message }
        }
      } = await upsertPdrRequirements({
        variables: {
          input: {
            cohortCode: canOverrideRequirements
              ? values.cohort
              : data?.pdrDefaultRequirementStipulations?.stipulations[0]?.cohortCode,
            cycleCode: cycleRequirements.cycleCode,
            effectiveDate: values.effectiveDate,
            pgaId,
            stipulations: values.requirements.map((requirement, i) => {
              return {
                required: Number(requirement.requiredCredits),
                ...(Number(formDefaultValues.requirements[i]?.deficit) > 0 || Number(requirement.deficit) !== 0
                  ? { deficit: Number(requirement.deficit) }
                  : {}),
                stipulationCode: requirement.stipulationCode
              };
            }),
            notifyMember: values.notifyMember,
            notes: values.notes
          }
        }
      });
      if (success) {
        showSnackbar(message || "Requirements adjusted");
        onDismiss();
      } else {
        setBannerError(message);
      }
    } catch (e) {
      setBannerError(`Error: ${(e as ApolloError).message}`);
    }
  };

  useEffect(() => {
    if (!data || !successStipulations) {
      return;
    }
    const stipulations = data.pdrDefaultRequirementStipulations?.stipulations;
    if (cohort === "AUTO") {
      setAutoCohortLabel(stipulations?.[0]?.cohortCode as AutoCohort);
    }
    if (!canOverrideRequirements) {
      cycleRequirements.requirements.map((requirement, i) => {
        const stipulation = find(stipulations, (stipulation: Stipulation) => {
          return stipulation.stipulationCode === requirement.stipulationCode;
        });
        setValue(`requirements.${i}.requiredCredits`, String(stipulation?.required || 0));
        setValue(`requirements.${i}.deficit`, String(getValues(`requirements.${i}.deficit`) || stipulation?.deficit));
      });
    }

    if (isNewRequirements && cohort !== "OR") {
      const newRequirements = stipulations.map((stipulation: Stipulation) => ({
        stipulationCode: stipulation.stipulationCode,
        requiredCredits: String(stipulation.required),
        deficit: stipulation.deficit,
        earned: 0,
        label: stipulation.label
      }));
      replace(
        newRequirements.map((req) => ({
          ...req,
          deficit: String(req.deficit)
        }))
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cohort, effectiveDate, data, isNewRequirements]);

  useEffect(() => {
    if (canOverrideRequirements) {
      const requirements = watch("requirements");
      requirements.forEach((_, index) => {
        resetField(`requirements.${index}.requiredCredits`);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cohort]);

  const modalActions = (
    <>
      <Button variant="text" onClick={onDismiss} disabled={isSubmitting}>
        Cancel
      </Button>
      <Button
        disabled={(isSubmitting && !isDirty) || (!successStipulations && !isCycle17)}
        onClick={handleSubmit(onSubmit)}
      >
        Save
      </Button>
    </>
  );

  return (
    <>
      <Modal
        onClosed={onDismiss}
        actions={
          <EmailPreviewModalActions
            onPreview={handlePreviewEmail}
            previewDisabled={!canPreviewEmail || isSubmitting}
            showPreview={notifyMember}
          >
            {modalActions}
          </EmailPreviewModalActions>
        }
        headline={`${isNewRequirements ? "Add" : "Adjust"} Requirements`}
        displayOverflow
        className={`${EMAIL_PREVIEW_MODAL_CLASSNAME} max-h-[85vh] overflow-y-auto`}
      >
        <FormProvider {...form}>
          <input className="absolute opacity-0" />
          {!!bannerError && (
            <div className="mb-6">
              <Banner inline variant="error" description="The requirements could not be saved." subText={bannerError} />
            </div>
          )}
          <div className="space-y-6">
            <Controller
              name="cohort"
              control={control}
              rules={{ required: "Cohort is required" }}
              render={({ field: { onChange, value } }) => (
                <Select
                  label="Cohort"
                  options={cohortOptions}
                  className="w-full"
                  value={value}
                  required
                  error={!!errors.cohort}
                  errorText={errors.cohort?.message as string}
                  onSelect={onChange}
                />
              )}
            />
            <SingleDatePicker
              control={control}
              label="Effective Date"
              name="effectiveDate"
              required
              minDate={cycleRequirements.cycleStartsOn}
              maxDate={cycleRequirements.cycleEndsOn}
            />
            {!successStipulations && !loading && !isCycle17 && (
              <Banner description={data?.pdrDefaultRequirementStipulations?.message || ""} variant="error" inline />
            )}
            {successStipulations && cohortDiffersFromToday && cohort !== "OR" && (
              <Banner
                inline
                variant="warn"
                description="The member's cohort as of this effective date is not the same as their cohort as of today."
              />
            )}

            {fields.map((requirement, i) => (
              <div className="flex flex-row gap-2" key={i}>
                <NumericField
                  className="w-full"
                  control={control}
                  name={`requirements.${i}.requiredCredits`}
                  label={`${requirement.label} Credits`}
                  value={watch(`requirements.${i}.requiredCredits`) || ""}
                  disabled={!canOverrideRequirements}
                  required
                />
                {Number(formDefaultValues.requirements[i]?.deficit) > 0 && (
                  <NumericField
                    className="w-full"
                    control={control}
                    name={`requirements.${i}.deficit`}
                    label={`${requirement.label} Deficit Credits`}
                    required
                  />
                )}
              </div>
            ))}
            <div className="flex items-center py-2">
              <Controller
                render={({ field }) => {
                  return <Checkbox checked={field.value} onChange={field.onChange} name={`notifyMember`} />;
                }}
                control={control}
                name={`notifyMember`}
              />
              <div className="pl-4">
                <h6 className="text-body-large">Notify Member of PDR Status</h6>
                <h6 className="text-body-medium">Send a notification to the member about their PDR status</h6>
              </div>
            </div>
            {note && <Banner description={note} inline />}
            <TextField className="w-full" control={control} name="notes" label="Note" type="textarea" required />
          </div>
        </FormProvider>
      </Modal>
      <EmailPreviewDialog
        open={previewOpen}
        onClosed={() => setPreviewOpen(false)}
        subject={preview?.subject}
        html={preview?.html}
        loading={previewLoading}
        errorMessage={previewError?.message}
      />
    </>
  );
}
